import { currentUrl, historyEvent, HistoryEventType, replaceState, URLX } from '@avensia/excite';
import { useState, useEffect, type ReactElement } from 'react';
import { type Selector, SelectType } from './ProductVariantsSelectorProvider';

import type { JSX } from 'react';

export const PRODUCT_CONFIG_URL_PARAMS = 'feature';

export const FEATURE_VALUE_SEPARATOR = '__';

function initialSelectOptions(features: NordicNest.Api.FeatureApiModel[]): { [item: string]: string } {
  let selectOptions: { [item: string]: string } = {};
  const params = currentUrl().searchParams.getAll(PRODUCT_CONFIG_URL_PARAMS);
  features.forEach(f => {
    const value = params
      .find(p => p.split(FEATURE_VALUE_SEPARATOR)[0] === f.cylindoFeature)
      ?.split(FEATURE_VALUE_SEPARATOR)[1];
    selectOptions = { ...selectOptions, [f.id]: f.options.find(o => o.cylindoOption === value)?.id ?? f.options[0].id };
  });

  return selectOptions;
}

export function useProductConfigurationSelectors(
  productConfiguration: NordicNest.Api.ProductConfigurationApiModel | undefined,
  addOrUpdate: (selector: Selector) => void,
  productConfigurationTransform: (
    selectedOption: NordicNest.Api.SelectOptionApiModel,
    feature: NordicNest.Api.FeatureApiModel,
    isSelectedValue: boolean,
  ) => ReactElement,
) {
  const [selectOptions, setSelectOptions] = useState<{ [item: string]: string }>(
    productConfiguration ? initialSelectOptions(productConfiguration.features) : {},
  );

  useEffect(
    () =>
      historyEvent.on(event => {
        if (event.event !== HistoryEventType.Replace && productConfiguration) {
          setSelectOptions(initialSelectOptions(productConfiguration.features));
        }
      }),
    [productConfiguration],
  );

  useEffect(() => {
    if (productConfiguration) {
      setSelectOptions(initialSelectOptions(productConfiguration.features));
      updateProductConfigurationUrlParams(productConfiguration);
    }
  }, [productConfiguration]);

  if (productConfiguration) {
    productConfiguration.features?.forEach(feature => {
      if (feature.options.length > 1) {
        const featureOptions = filterOptionsOnConstraints(productConfiguration, selectOptions).filter(
          f => f.featureId === feature.id,
        );

        const options: NordicNest.Api.SelectOptionApiModel[] = featureOptions.map(o => ({
          text: o.label,
          value: o.cylindoOption,
        }));

        const value = options.find(o => o.value === selectOptions[feature.id])?.value ?? options[0]?.value;
        const selectedFeatureOption = feature.options.find(o => o.id === value);

        const selectedOption = options?.find(o => o.value === value);

        const onChangeOption = (value: string) => {
          const newSelections = { ...selectOptions, [feature.id]: value };
          const options = filterOptionsOnConstraints(productConfiguration, newSelections);
          const selectOption = options.find(o => o.id === value);
          const url = updateUrlWithProductConfig(feature, options, selectOption?.cylindoOption || '');
          replaceState(url, { skipLoad: true });
          productConfiguration.features.forEach(f => {
            if (!options.filter(o => o.featureId === f.id).find(t => t.id === newSelections[f.id])) {
              const opt = options.find(o => o.featureId === f.id);
              if (opt) {
                newSelections[f.id] = opt.id;
              }
            }
          });
          setSelectOptions(newSelections);
        };

        addOrUpdate({
          id: feature.cylindoFeature,
          value,
          selectedOption,
          options,
          onChangeOption,
          selectedTransform: option => productConfigurationTransform(option, feature, true),
          optionTransform: option => productConfigurationTransform(option, feature, false),
          showVariantSelect: true,
          type: SelectType.ProductConfiguration,
          additionalPrice: selectedFeatureOption?.priceAdjustment ?? { inclVat: 0, exclVat: 0, vatPercent: 0 },
          placeholder: feature.label,
        });
      }
    });
  }

  return selectOptions;
}
function updateProductConfigurationUrlParams(productConfiguration: NordicNest.Api.ProductConfigurationApiModel) {
  const url = currentUrl();
  const params = url.searchParams.getAll(PRODUCT_CONFIG_URL_PARAMS);
  const newUrl = new URLX(url);
  newUrl.searchParams.delete(PRODUCT_CONFIG_URL_PARAMS);

  params.forEach(param => {
    if (productConfiguration.features.find(f => param.split(FEATURE_VALUE_SEPARATOR)[0] === f.cylindoFeature)) {
      newUrl.searchParams.append(PRODUCT_CONFIG_URL_PARAMS, param);
    }
  });
  replaceState(newUrl, { skipLoad: true });
}

function updateUrlWithProductConfig(
  feature: NordicNest.Api.FeatureApiModel,
  availableOptions: NordicNest.Api.OptionApiModel[],
  value: string,
) {
  const url = currentUrl();
  const features = url.searchParams.getAll(PRODUCT_CONFIG_URL_PARAMS);
  url.searchParams.delete(PRODUCT_CONFIG_URL_PARAMS);
  features
    .filter(f => f.split(FEATURE_VALUE_SEPARATOR)[0] !== feature.cylindoFeature)
    .forEach(f => {
      if (availableOptions.find(o => `${o.cylindoFeature}${FEATURE_VALUE_SEPARATOR}${o.cylindoOption}` === f)) {
        url.searchParams.append(PRODUCT_CONFIG_URL_PARAMS, f);
      } else {
        const feature = f.split(FEATURE_VALUE_SEPARATOR)[0];
        const availabilityOptionsForFeature = availableOptions.filter(i => i.cylindoFeature === feature);
        if (availabilityOptionsForFeature.length > 0) {
          url.searchParams.append(
            PRODUCT_CONFIG_URL_PARAMS,
            `${feature}${FEATURE_VALUE_SEPARATOR}${availabilityOptionsForFeature[0].cylindoOption}`,
          );
        }
      }
    });

  url.searchParams.append(PRODUCT_CONFIG_URL_PARAMS, `${feature.cylindoFeature}${FEATURE_VALUE_SEPARATOR}${value}`);
  return url;
}

function filterOptionsOnConstraints(
  productConfiguration: NordicNest.Api.ProductConfigurationApiModel,
  selectOptions: { [item: string]: string },
) {
  const constrains: { [item: string]: string[] } = Object.assign(
    {},
    ...productConfiguration.features
      .flatMap(f => f.options.filter(o => selectOptions[f.id] === o.id))
      .map(o => o.constraints),
  );

  const options = productConfiguration.features.flatMap(f =>
    f.options.filter(o => constrains[o.featureId]?.includes(o.id) ?? true),
  );
  return options;
}
